import { useEffect, useRef } from 'react';

/**
 * Scrolls its element to the top of the scrollport once per `key`.
 *
 * **How far below the top it lands is the region's**, as `pinOffset` - a `scroll-padding-top` the
 * scrollport carries rather than a margin written onto every target.
 */
export const usePinToTop = <ElementType extends HTMLElement>({
  key,
  instant = false,
}: {
  key: string | null;
  instant?: boolean;
}) => {
  const ref = useRef<ElementType | null>(null);
  const pinnedKey = useRef<string | null>(null);

  useEffect(() => {
    const element = ref.current;

    if (!key) {
      pinnedKey.current = null;

      return;
    }

    if (!element || pinnedKey.current === key) return;

    pinnedKey.current = key;

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    element.scrollIntoView({
      behavior: instant || prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  });

  return ref;
};
